import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { 
  ArrowLeft, FileText, Search, User, Shield, AlertTriangle, 
  Loader2, ChevronLeft, ChevronRight, Smartphone, Key,
  Download, Trash2, Settings, Calendar, Clock
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import Pagination from '../../components/common/Pagination';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LOG_TYPE_CONFIG = {
  admin_login: { label: 'Admin Login', icon: Shield, color: 'bg-blue-100 text-blue-800' },
  user_login: { label: 'User Login', icon: User, color: 'bg-green-100 text-green-800' },
  failed_login: { label: 'Failed Login', icon: AlertTriangle, color: 'bg-red-100 text-red-800' },
  logout: { label: 'Logout', icon: User, color: 'bg-gray-100 text-gray-800' },
  security_event: { label: 'Security Event', icon: Smartphone, color: 'bg-orange-100 text-orange-800' },
  session_logout: { label: 'Session Logout', icon: Smartphone, color: 'bg-purple-100 text-purple-800' },
  password_change: { label: 'Password Change', icon: Key, color: 'bg-yellow-100 text-yellow-800' },
  module_update: { label: 'Module Update', icon: Settings, color: 'bg-cyan-100 text-cyan-800' }
};

const LOG_TYPE_OPTIONS = [
  { value: 'admin_login', label: 'Admin Login' },
  { value: 'user_login', label: 'User Login' },
  { value: 'failed_login', label: 'Failed Login' },
  { value: 'security_event', label: 'Security Event' },
  { value: 'session_logout', label: 'Session Logout' },
  { value: 'password_change', label: 'Password Change' },
  { value: 'module_update', label: 'Module Update' }
];

const AuditLogsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [logTypeFilter, setLogTypeFilter] = useState('all');
  const [searchIndos, setSearchIndos] = useState('');

  // Export Modal State
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exportLogTypes, setExportLogTypes] = useState([]);
  const [exportFormat, setExportFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);

  // Delete Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStartDate, setDeleteStartDate] = useState('');
  const [deleteEndDate, setDeleteEndDate] = useState('');
  const [deleteLogTypes, setDeleteLogTypes] = useState([]);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Retention Settings Modal State
  const [showRetentionModal, setShowRetentionModal] = useState(false);
  const [retentionSettings, setRetentionSettings] = useState(null);
  const [retentionEnabled, setRetentionEnabled] = useState(false);
  const [retentionDays, setRetentionDays] = useState(90);
  const [savingRetention, setSavingRetention] = useState(false);
  const [runningCleanup, setRunningCleanup] = useState(false);

  useEffect(() => {
    fetchLogs();
    fetchStats();
    fetchRetentionSettings();
  }, [page, logTypeFilter, perPage]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      let url = `${API}/admin-tools/audit-logs?page=${page}&per_page=${perPage}`;
      if (logTypeFilter !== 'all') url += `&log_type=${logTypeFilter}`;
      if (searchIndos) url += `&user_indos=${searchIndos}`;
      
      const response = await axios.get(url);
      setLogs(response.data.logs || []);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin-tools/audit-logs/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchRetentionSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin-tools/audit-logs/retention`);
      setRetentionSettings(response.data);
      setRetentionEnabled(response.data.enabled || false);
      setRetentionDays(response.data.retention_days || 90);
    } catch (error) {
      console.error('Failed to fetch retention settings:', error);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchLogs();
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Export Functions
  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await axios.post(`${API}/admin-tools/audit-logs/export`, {
        start_date: exportStartDate || null,
        end_date: exportEndDate || null,
        log_types: exportLogTypes.length > 0 ? exportLogTypes : null,
        format: exportFormat
      }, { responseType: 'blob' });

      // Create download link
      const blob = new Blob([response.data], { 
        type: exportFormat === 'csv' ? 'text/csv' : 'application/json' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Audit logs exported successfully');
      setShowExportModal(false);
      resetExportForm();
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  };

  const resetExportForm = () => {
    setExportStartDate('');
    setExportEndDate('');
    setExportLogTypes([]);
    setExportFormat('csv');
  };

  // Delete Functions
  const handleDeletePreview = async () => {
    try {
      setPreviewLoading(true);
      const response = await axios.post(`${API}/admin-tools/audit-logs/delete/preview`, {
        start_date: deleteStartDate || null,
        end_date: deleteEndDate || null,
        log_types: deleteLogTypes.length > 0 ? deleteLogTypes : null,
        confirm_text: ''
      });
      setDeletePreview(response.data);
    } catch (error) {
      console.error('Preview failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to preview deletion');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    try {
      setDeleting(true);
      const response = await axios.post(`${API}/admin-tools/audit-logs/delete`, {
        start_date: deleteStartDate || null,
        end_date: deleteEndDate || null,
        log_types: deleteLogTypes.length > 0 ? deleteLogTypes : null,
        confirm_text: deleteConfirmText
      });

      toast.success(response.data.message);
      setShowDeleteModal(false);
      resetDeleteForm();
      fetchLogs();
      fetchStats();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete audit logs');
    } finally {
      setDeleting(false);
    }
  };

  const resetDeleteForm = () => {
    setDeleteStartDate('');
    setDeleteEndDate('');
    setDeleteLogTypes([]);
    setDeletePreview(null);
    setDeleteConfirmText('');
  };

  // Retention Functions
  const handleSaveRetention = async () => {
    try {
      setSavingRetention(true);
      const response = await axios.post(`${API}/admin-tools/audit-logs/retention`, {
        enabled: retentionEnabled,
        retention_days: retentionDays
      });

      toast.success(response.data.message);
      fetchRetentionSettings();
    } catch (error) {
      console.error('Failed to save retention settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save retention settings');
    } finally {
      setSavingRetention(false);
    }
  };

  const handleRunCleanup = async () => {
    try {
      setRunningCleanup(true);
      const response = await axios.post(`${API}/admin-tools/audit-logs/retention/run-cleanup`);
      toast.success(response.data.message);
      fetchRetentionSettings();
      fetchLogs();
      fetchStats();
    } catch (error) {
      console.error('Cleanup failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to run cleanup');
    } finally {
      setRunningCleanup(false);
    }
  };

  const toggleLogType = (type, checked, setter, current) => {
    if (checked) {
      setter([...current, type]);
    } else {
      setter(current.filter(t => t !== type));
    }
  };

  const isFullAdmin = user?.role === 'admin';

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/admin/tools')} className="text-gray-600">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Tools
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-[#0A7EA4]" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
              <p className="text-gray-600">Monitor login activity and security events</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowExportModal(true)}
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            {isFullAdmin && (
              <>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowDeleteModal(true)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowRetentionModal(true)}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Retention
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Retention Status Badge */}
        {retentionSettings?.enabled && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-blue-800">
              Auto-retention enabled: Logs older than {retentionSettings.retention_days} days are automatically deleted.
              {retentionSettings.last_cleanup && (
                <span className="text-blue-600 ml-2">
                  Last cleanup: {formatDate(retentionSettings.last_cleanup)} ({retentionSettings.last_cleanup_count} logs removed)
                </span>
              )}
            </span>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#0A7EA4]">{stats.total}</p>
                <p className="text-sm text-gray-600">Total Logs</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.last_24h?.successful_logins || 0}</p>
                <p className="text-sm text-gray-600">Logins (24h)</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{stats.last_24h?.failed_logins || 0}</p>
                <p className="text-sm text-gray-600">Failed (24h)</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.by_type?.admin_logins || 0}</p>
                <p className="text-sm text-gray-600">Admin Logins</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Search by INDoS</label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter INDoS number..."
                    value={searchIndos}
                    onChange={(e) => setSearchIndos(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button onClick={handleSearch} size="sm">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="w-[180px]">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Log Type</label>
                <Select value={logTypeFilter} onValueChange={(v) => { setLogTypeFilter(v); setPage(1); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="admin_login">Admin Login</SelectItem>
                    <SelectItem value="user_login">User Login</SelectItem>
                    <SelectItem value="failed_login">Failed Login</SelectItem>
                    <SelectItem value="security_event">Security Event</SelectItem>
                    <SelectItem value="session_logout">Session Logout</SelectItem>
                    <SelectItem value="password_change">Password Change</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle>Activity Log</CardTitle>
            <CardDescription>{total} records found</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                <p className="text-gray-500 mt-2">Loading logs...</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No audit logs found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-medium">Type</th>
                      <th className="text-left p-3 font-medium">User</th>
                      <th className="text-left p-3 font-medium">IP Address</th>
                      <th className="text-left p-3 font-medium">Device</th>
                      <th className="text-left p-3 font-medium">Event</th>
                      <th className="text-left p-3 font-medium">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => {
                      const typeConfig = LOG_TYPE_CONFIG[log.log_type] || LOG_TYPE_CONFIG.user_login;
                      const TypeIcon = typeConfig.icon;
                      
                      const getDeviceDisplay = () => {
                        if (!log.details) return '-';
                        if (log.log_type === 'security_event') {
                          const eventType = log.details.event_type;
                          if (eventType === 'device_limit_exceeded') {
                            return (
                              <span className="text-xs">
                                <span className="text-red-500">{log.details.logged_out_device}</span>
                                <span className="text-gray-400 mx-1">→</span>
                                <span className="text-green-600">{log.details.new_device}</span>
                              </span>
                            );
                          }
                        }
                        return log.details.device_name || '-';
                      };
                      
                      const getEventDisplay = () => {
                        if (!log.details) return '-';
                        if (log.log_type === 'security_event') {
                          const eventType = log.details.event_type;
                          if (eventType === 'device_limit_exceeded') {
                            return <span className="text-orange-600">Device Limit Exceeded</span>;
                          }
                          if (eventType === 'new_device_login') {
                            return <span className="text-blue-600">New Device</span>;
                          }
                          if (log.details.action === 'bulk_delete_audit_logs') {
                            return <span className="text-red-600">Bulk Delete ({log.details.deleted_count})</span>;
                          }
                          if (log.details.action === 'retention_cleanup') {
                            return <span className="text-blue-600">Auto Cleanup ({log.details.deleted_count})</span>;
                          }
                          return eventType || log.details.action;
                        }
                        if (log.details.is_new_device) {
                          return <span className="text-blue-600">New Device Login</span>;
                        }
                        if (log.details.login_method === 'password') return 'Password';
                        if (log.details.login_method === '2fa') return '2FA Verified';
                        if (log.log_type === 'failed_login') {
                          return <span className="text-red-600">Invalid Credentials</span>;
                        }
                        return '-';
                      };
                      
                      return (
                        <tr key={log.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <Badge className={typeConfig.color}>
                              <TypeIcon className="w-3 h-3 mr-1" />
                              {typeConfig.label}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <div>
                              <p className="font-medium">{log.user_indos || 'Unknown'}</p>
                              <p className="text-xs text-gray-500">{log.user_name || '-'}</p>
                              {log.user_role && (
                                <Badge variant="outline" className="text-xs mt-1">{log.user_role}</Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {log.ip_address || 'N/A'}
                            </code>
                          </td>
                          <td className="p-3 text-sm">{getDeviceDisplay()}</td>
                          <td className="p-3 text-sm">{getEventDisplay()}</td>
                          <td className="p-3 text-gray-600">{formatDate(log.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {total > 0 && (
              <div className="mt-4 pt-4 border-t">
                <Pagination
                  currentPage={page}
                  totalItems={total}
                  itemsPerPage={perPage}
                  onPageChange={setPage}
                  onItemsPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Export Modal */}
        <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Download className="w-5 h-5 text-[#0A7EA4]" />
                Export Audit Logs
              </DialogTitle>
              <DialogDescription>
                Choose filters and format for your export
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Start Date</Label>
                  <Input
                    type="date"
                    value={exportStartDate}
                    onChange={(e) => setExportStartDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">End Date</Label>
                  <Input
                    type="date"
                    value={exportEndDate}
                    onChange={(e) => setExportEndDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Log Types */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Log Types (leave empty for all)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {LOG_TYPE_OPTIONS.map(type => (
                    <div key={type.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`export-${type.value}`}
                        checked={exportLogTypes.includes(type.value)}
                        onCheckedChange={(checked) => toggleLogType(type.value, checked, setExportLogTypes, exportLogTypes)}
                      />
                      <label htmlFor={`export-${type.value}`} className="text-sm">{type.label}</label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Format */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Export Format</Label>
                <div className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="format-csv"
                      name="format"
                      value="csv"
                      checked={exportFormat === 'csv'}
                      onChange={() => setExportFormat('csv')}
                    />
                    <label htmlFor="format-csv" className="text-sm">CSV (Spreadsheet)</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="format-json"
                      name="format"
                      value="json"
                      checked={exportFormat === 'json'}
                      onChange={() => setExportFormat('json')}
                    />
                    <label htmlFor="format-json" className="text-sm">JSON (Technical)</label>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExportModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleExport} disabled={exporting}>
                {exporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={showDeleteModal} onOpenChange={(open) => { setShowDeleteModal(open); if (!open) resetDeleteForm(); }}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Delete Audit Logs
              </DialogTitle>
              <DialogDescription>
                Permanently delete audit logs. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Start Date</Label>
                  <Input
                    type="date"
                    value={deleteStartDate}
                    onChange={(e) => { setDeleteStartDate(e.target.value); setDeletePreview(null); }}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">End Date</Label>
                  <Input
                    type="date"
                    value={deleteEndDate}
                    onChange={(e) => { setDeleteEndDate(e.target.value); setDeletePreview(null); }}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Log Types */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Log Types (leave empty for all)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {LOG_TYPE_OPTIONS.map(type => (
                    <div key={type.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`delete-${type.value}`}
                        checked={deleteLogTypes.includes(type.value)}
                        onCheckedChange={(checked) => {
                          toggleLogType(type.value, checked, setDeleteLogTypes, deleteLogTypes);
                          setDeletePreview(null);
                        }}
                      />
                      <label htmlFor={`delete-${type.value}`} className="text-sm">{type.label}</label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview Button */}
              <Button 
                variant="outline" 
                onClick={handleDeletePreview} 
                disabled={previewLoading}
                className="w-full"
              >
                {previewLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Calculating...
                  </>
                ) : (
                  'Preview Deletion'
                )}
              </Button>

              {/* Preview Results */}
              {deletePreview && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="font-semibold text-red-800 mb-2">
                    {deletePreview.total_to_delete.toLocaleString()} logs will be deleted
                  </p>
                  {Object.keys(deletePreview.breakdown).length > 0 && (
                    <div className="text-sm text-red-700">
                      <p className="font-medium mb-1">Breakdown:</p>
                      <ul className="list-disc list-inside">
                        {Object.entries(deletePreview.breakdown).map(([type, count]) => (
                          <li key={type}>{LOG_TYPE_CONFIG[type]?.label || type}: {count}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Confirmation */}
              {deletePreview && deletePreview.total_to_delete > 0 && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    Type <span className="font-bold text-red-600">DELETE</span> to confirm
                  </Label>
                  <Input
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="Type DELETE"
                    className="border-red-300 focus:border-red-500"
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowDeleteModal(false); resetDeleteForm(); }}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDelete} 
                disabled={deleting || !deletePreview || deletePreview.total_to_delete === 0 || deleteConfirmText !== 'DELETE'}
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete {deletePreview?.total_to_delete || 0} Logs
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Retention Settings Modal */}
        <Dialog open={showRetentionModal} onOpenChange={setShowRetentionModal}>
          <DialogContent className="sm:max-w-[450px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#0A7EA4]" />
                Auto-Retention Policy
              </DialogTitle>
              <DialogDescription>
                Automatically delete old audit logs to manage database size
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Enable Switch */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Enable Auto-Retention</Label>
                  <p className="text-xs text-gray-500">Automatically delete old logs</p>
                </div>
                <Switch
                  checked={retentionEnabled}
                  onCheckedChange={setRetentionEnabled}
                />
              </div>

              {/* Retention Period */}
              {retentionEnabled && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">Retention Period</Label>
                  <Select value={retentionDays.toString()} onValueChange={(v) => setRetentionDays(Number(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="90">90 days (minimum)</SelectItem>
                      <SelectItem value="180">180 days (6 months)</SelectItem>
                      <SelectItem value="365">365 days (1 year)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Logs older than {retentionDays} days will be automatically deleted. 90-day floor enforced by policy.
                  </p>
                </div>
              )}

              {/* Last Cleanup Info */}
              {retentionSettings?.last_cleanup && (
                <div className="p-3 bg-gray-50 rounded-lg text-sm">
                  <p className="font-medium text-gray-700">Last Cleanup</p>
                  <p className="text-gray-600">
                    {formatDate(retentionSettings.last_cleanup)} - {retentionSettings.last_cleanup_count} logs removed
                  </p>
                </div>
              )}

              {/* Manual Cleanup Button */}
              {retentionEnabled && (
                <Button 
                  variant="outline" 
                  onClick={handleRunCleanup}
                  disabled={runningCleanup}
                  className="w-full"
                >
                  {runningCleanup ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Running Cleanup...
                    </>
                  ) : (
                    <>
                      <Clock className="w-4 h-4 mr-2" />
                      Run Cleanup Now
                    </>
                  )}
                </Button>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRetentionModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveRetention} disabled={savingRetention}>
                {savingRetention ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Settings'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AuditLogsPage;
